// ============================================
// FILE: src/features/locker-controllers/components/LockerControllerList.tsx
// ============================================

import React, { useMemo, useState, useEffect } from 'react';
import {
  List,
  TextField,
  FunctionField,
  useTranslate,
  ListProps,
  usePermissions,
  ReferenceInput,
  SelectInput,
  useGetIdentity,
  useGetList,
} from 'react-admin';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Hotel as HotelIcon, Router as RouterIcon } from '@mui/icons-material';
import CustomDataGrid from '../../../components/common/CustomDatagrid';
import LockerControllerActions from './LockerControllerActions';
import ConnectionStatusChip from './ui/ConnectionStatusChip';
import { LockerControllerListItem } from '../types';
import { CustomTextInputFilter, CustomSelectInputFilter } from '@/components/common/CustomFilters';
import CustomListToolbar from '@/components/common/CustomListToolbar';

const LockerControllerList: React.FC<ListProps> = (props) => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();
  
  // Estado local para el hotel seleccionado
  const [selectedHotelId, setSelectedHotelId] = useState<string | null>(null);
  
  // Determinar roles y permisos
  const userRole = permissions || '';
  const isSuperAdmin = userRole === 'SuperAdmin';
  const isGroupAdmin = userRole === 'GroupAdmin';
  const isHotelAdmin = userRole === 'HotelAdmin';
  const isReceptionist = userRole === 'Receptionist';
  const canSelectHotel = isSuperAdmin || isGroupAdmin;
  
  // Check permissions
  const canCreate = useMemo(() => {
    return permissions === 'SuperAdmin';
  }, [permissions]);
  
  const canExport = true;
  
  // Determinar el filtro de hoteles según el rol
  const hotelFilter = useMemo(() => {
    if (isHotelAdmin || isReceptionist) {
      // Hotel fijo del usuario
      return identity?.hotelId ? { id: identity.hotelId } : {};
    } else if (isGroupAdmin) {
      // Solo hoteles del grupo
      return identity?.groupId ? { groupId: identity.groupId } : {};
    }
    // SuperAdmin ve todos
    return {};
  }, [isHotelAdmin, isReceptionist, isGroupAdmin, identity?.hotelId, identity?.groupId]);
  
  // Cargar hoteles disponibles según el rol
  const { data: hotels } = useGetList('hotels', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: hotelFilter,
  }, {
    enabled: !identityLoading,
  });
  
  // Establecer hotel inicial cuando se cargan los datos
  useEffect(() => {
    if (!identityLoading && hotels && hotels.length > 0 && !selectedHotelId) {
      if ((isHotelAdmin || isReceptionist) && identity?.hotelId) {
        // HotelAdmin/Receptionist: usar su hotel fijo
        setSelectedHotelId(identity.hotelId);
      } else {
        // SuperAdmin/GroupAdmin: seleccionar el primer hotel
        setSelectedHotelId(hotels[0].id);
      }
    }
  }, [identityLoading, identity?.hotelId, hotels, selectedHotelId, isHotelAdmin, isReceptionist]);
  
  // Define filters con hotel siempre visible
  const filters = useMemo(() => [
    // Búsqueda
    <CustomTextInputFilter
      key="search"
      source="q"
      label={translate('resources.locker-controllers.filters.search')}
      alwaysOn
    />,
    
    // SELECTOR DE HOTEL - Siempre visible al principio
    <ReferenceInput
      key="hotelId"
      source="hotelId"
      reference="hotels"
      label={translate('common.hotel')}
      alwaysOn
      sort={{ field: 'name', order: 'ASC' }}
      filter={hotelFilter}
      perPage={100}
    >
      <CustomSelectInputFilter 
        optionText="name"
        optionValue="id"
        disabled={isHotelAdmin || isReceptionist} // Deshabilitado para roles de hotel único
      />
    </ReferenceInput>,
    
    // Zona - filtrada por hotel seleccionado
    <ReferenceInput
      key="zoneId"
      source="zoneId"
      reference="zones"
      label={translate('resources.locker-controllers.fields.zone')}
      filter={selectedHotelId ? { hotelId: selectedHotelId } : {}}
      alwaysOn
    >
      <CustomSelectInputFilter optionText="name" />
    </ReferenceInput>,
    
    // Estado activo
    <CustomSelectInputFilter
      key="isActive"
      source="isActive"
      label={translate('resources.locker-controllers.fields.isActive')}
      choices={[
        { id: true, name: translate('ra.boolean.true') },
        { id: false, name: translate('ra.boolean.false') },
      ]}
      alwaysOn
    />,
  ], [translate, selectedHotelId, hotelFilter, isHotelAdmin, isReceptionist]);
  
  // Valores por defecto del filtro - siempre incluir hotelId
  const filterDefaultValues = useMemo(() => {
    if (selectedHotelId) {
      return { hotelId: selectedHotelId };
    }
    return {};
  }, [selectedHotelId]);
  
  // Si todavía está cargando o no hay hotel seleccionado
  if (identityLoading || !selectedHotelId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Main list render
  return (
    <List
      {...props}
      title={translate('resources.locker-controllers.titles.list')}
      filters={filters}
      filterDefaultValues={filterDefaultValues}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
      // Key para forzar re-render cuando cambia el hotel
      key={`locker-controllers-${selectedHotelId}`}
      actions={
        <CustomListToolbar 
          showFilterButton={false}
          showCreate={canCreate}
          createLabel={translate('resources.locker-controllers.actions.create', 'Nuevo Controlador')}
        />
      }
    >
      <CustomDataGrid
        rowClick="edit"
        bulkActionButtons={canCreate ? undefined : false}
      >
        {/* Actions column */}
        <FunctionField
          label=""
          render={(record: LockerControllerListItem) => (
            <LockerControllerActions record={record} />
          )}
          sortable={false}
        />
        
        {/* Name with icon */}
        <FunctionField
          label={translate('resources.locker-controllers.fields.name')}
          render={(record: LockerControllerListItem) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RouterIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {record.name}
              </Typography>
            </Box>
          )}
          sortBy="name"
        />
        
        {/* IP Address and Port */}
        <FunctionField
          label={translate('resources.locker-controllers.fields.network')}
          render={(record: LockerControllerListItem) => (
            <Typography variant="body2">
              {record.ipAddress}:{record.tcpPort}
            </Typography>
          )}
          sortBy="ipAddress"
        />
        
        {/* Zone */}
        <FunctionField
          label={translate('resources.locker-controllers.fields.zone')}
          render={(record: LockerControllerListItem) => (
            <Typography variant="body2">
              {record.zone?.name || '-'}
            </Typography>
          )}
        />
        
        {/* Connection Status */}
        <FunctionField
          label={translate('resources.locker-controllers.fields.status')}
          render={(record: LockerControllerListItem) => (
            <ConnectionStatusChip lastSeenAt={record.lastSeenAt} />
          )}
          sortBy="lastSeenAt"
        />
        
        {/* Last Seen */}
        <FunctionField
          label={translate('resources.locker-controllers.fields.lastSeen')}
          render={(record: LockerControllerListItem) => (
            <Typography variant="caption" color="text.secondary">
              {record.lastSeenAt ? new Date(record.lastSeenAt).toLocaleString() : '-'}
            </Typography>
          )}
          sortBy="lastSeenAt"
        />
        
        {/* Hotel - Solo mostrar si es SuperAdmin o GroupAdmin */}
        {canSelectHotel && (
          <TextField 
            source="hotel.name" 
            label={translate('resources.locker-controllers.fields.hotel')}
            sortBy="hotel.name"
          />
        )}
      </CustomDataGrid>
    </List>
  );
};

export default LockerControllerList;